import React, { useEffect, useRef } from 'react';

import type { DeepReadonly } from 'js/src/libs/consts';

type Props = {
    data?: DeepReadonly<{
        script_source: string;
    }>;
    bgColor: string;
};

const ThirdPartyScriptComponent = React.memo(function ThirdPartyScriptComponent({
    data,
    bgColor,
}: Props) {
    let htmlFromContentful = '';
    if (data) {
        htmlFromContentful = data.script_source;
    }
    const wrapper = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (wrapper.current === null) {
            return;
        }

        const waitUntilScriptIsLoaded = async (
            newScriptNode: HTMLScriptElement,
            parentElement: HTMLElement,
        ) => {
            return new Promise<HTMLScriptElement>((resolve, reject) => {
                try {
                    newScriptNode.onload = () => {
                        resolve(newScriptNode);
                    };
                } catch (error) {
                    reject(error);
                }
                parentElement.appendChild(newScriptNode);
            });
        };

        const parseAndCreateDomNodes = async () => {
            if (wrapper.current === null) throw new Error('Wrapper is null');

            // 1. convert html string representation to DOM nodes:
            const doc = new DOMParser().parseFromString(htmlFromContentful.trim(), 'text/html');

            // 2. Grab all the Script Nodes
            const allScriptNodes = doc.getElementsByTagName('script');

            // 3. Now filter out the script tag and add an custom attribute to them.
            let scriptNodeCount = 0;
            for (let i = 0; i < allScriptNodes.length; i++, scriptNodeCount++) {
                allScriptNodes[i].setAttribute('data-l5-external-script-id', i.toString());
            }

            // 4. Add all the nodes to the actual rendered DOM.
            const headChildCount = doc.head.childElementCount;
            const bodyChildCount = doc.body.childElementCount;

            const headChildNodes = doc.head.children;
            const bodyChildNodes = doc.body.children;

            for (let i = 0; i < headChildCount; i++) {
                wrapper.current.appendChild(headChildNodes[0]);
            }

            for (let i = 0; i < bodyChildCount; i++) {
                wrapper.current.appendChild(bodyChildNodes[0]);
            }

            // 5. go through the script tags one by one,replacing each with a new one that will actually run.
            for (let i = 0; i < scriptNodeCount; i++) {
                const oldScriptNode = wrapper.current.querySelector<HTMLScriptElement>(
                    `[data-l5-external-script-id="${i}"]`,
                );

                if (oldScriptNode) {
                    const parent = oldScriptNode.parentElement;
                    if (parent === null) throw new Error('Parent is null');
                    const newScriptNode = document.createElement('script');

                    const oldAttributes = oldScriptNode.getAttributeNames();
                    oldAttributes.forEach((attr) => {
                        newScriptNode.setAttribute(attr, oldScriptNode.getAttribute(attr)!);
                    });

                    newScriptNode.appendChild(document.createTextNode(oldScriptNode.innerText));
                    parent.removeChild(oldScriptNode);

                    if (newScriptNode.hasAttribute('src')) {
                        await waitUntilScriptIsLoaded(newScriptNode, parent);
                    } else {
                        newScriptNode.nonce = window._CSP_NONCE;
                        parent.appendChild(newScriptNode);
                    }
                }
            }
        };

        if (htmlFromContentful) {
            parseAndCreateDomNodes();
        }
    }, [htmlFromContentful, wrapper]);

    return (
        <div
            className="contentful-component external-script-container"
            style={{ backgroundColor: bgColor }}
        >
            <div className="container">
                <div className="row">
                    <div className="col-sm-12 mb-l5-16">
                        <div ref={wrapper} />
                    </div>
                </div>
            </div>
        </div>
    );
});

export default ThirdPartyScriptComponent;
